/**
 * The session's fills (#1809, WS_DESIGN_1809 W6): the newest 2000 read once, then the userFills stream (sessionRows.ts).
 * What is missing after a reconnect is read by time from the newest held row.
 */
import { abortError } from '../hl/info';
import type { UserFill, UserFillsMsg } from '../hl/types';
import { FILL_BASE_WEIGHT, FILL_ROWS_PER_WEIGHT } from '../settlement/store';
import { FILLS_PAGE_CAP, mergeFills } from './orders/model';
import { SessionRows, type RowsStatus, type SessionRowsDeps } from './sessionRows';

/** How long the account must stay on screen before its fills are downloaded. */
export const FILLS_DOWNLOAD_DELAY_MS = 1_000;

export interface FillsHeld extends RowsStatus {
  /** Newest first, one row per tid. */
  fills: UserFill[];
  /** Older fills exist that are not held. */
  capped: boolean;
}

const newestTime = (rows: readonly UserFill[]): number => rows.reduce((t, f) => Math.max(t, f.time), 0);
const oldestTime = (rows: readonly UserFill[]): number => rows.reduce((t, f) => Math.min(t, f.time), Infinity);

export class SessionFills extends SessionRows<FillsHeld, UserFill> {
  /** Rows from this time on may be missing. */
  private gapFrom: number | null = null;

  constructor(deps: SessionRowsDeps) {
    super(deps, { fills: [], loading: true, complete: false, capped: false, error: null });
  }

  protected stream(): () => void {
    const { ws, user } = this.deps;
    return ws.subscribe({ type: 'userFills', user }, (msg) => this.onFrame(msg));
  }

  protected readOwed(): boolean {
    return !this.state.complete || this.gapFrom !== null;
  }

  protected keyOf(f: UserFill): number {
    return f.tid;
  }

  protected held(): readonly UserFill[] {
    return this.state.fills;
  }

  protected merged(rows: UserFill[]): Partial<FillsHeld> {
    return { fills: mergeFills(this.state.fills, rows) };
  }

  private onFrame(msg: UserFillsMsg): void {
    if (Array.isArray(msg?.fills)) this.onRows(msg.fills, msg.isSnapshot === true);
  }

  protected read(): void {
    if (this.gapFrom !== null) this.readGap(this.gapFrom);
    else void this.download();
  }

  protected readMissing(): void {
    this.readGap(newestTime(this.state.fills));
  }

  private async download(): Promise<void> {
    const { info, pacer, user } = this.deps;
    const ac = this.beginRead();
    try {
      await pacer.take(FILL_BASE_WEIGHT, { signal: ac.signal });
      const rows = await this.paid(info.userFills(user, false, { signal: ac.signal }), ac);
      const missed = this.missedDuringRead(rows);
      this.publish({
        ...this.merged(rows),
        loading: false,
        complete: true,
        capped: rows.length >= FILLS_PAGE_CAP,
        error: null,
      });
      if (missed) this.readGap(newestTime(rows));
    } catch (e) {
      this.failed(ac, e);
    }
  }

  /** Reads what may be missing from `since` on. A second gap while one is being read widens that read. */
  private readGap(since: number): void {
    this.gapFrom = Math.min(this.gapFrom ?? since, since);
    void this.fetchGap(this.gapFrom);
  }

  private async fetchGap(since: number): Promise<void> {
    const { info, pacer, user } = this.deps;
    const ac = this.beginRead();
    const { signal } = ac;
    try {
      pacer.charge(FILL_BASE_WEIGHT);
      const byTime = { type: 'userFillsByTime', user, startTime: since };
      const page = await this.paid(info.request<UserFill[]>(byTime, { signal }), ac);
      // A full page is the oldest 2000 of the gap, so the newest are read too.
      let newest: UserFill[] = [];
      if (page.length >= FILLS_PAGE_CAP) {
        pacer.charge(FILL_BASE_WEIGHT);
        newest = await this.paid(info.userFills(user, false, { signal }), ac);
      }
      this.gapFrom = null;
      if (newest.length > 0 && !this.overlaps(page, newest)) {
        // Over 4000 missed: the two sides cannot be joined, and a list must not hide a hole. Keep the newest side.
        const from = oldestTime(newest);
        const held = this.state.fills.filter((f) => f.time >= from);
        this.publish({ fills: mergeFills(held, newest), loading: false, capped: true, error: null });
      } else {
        this.publish({ fills: mergeFills(mergeFills(this.state.fills, page), newest), loading: false, error: null });
      }
    } catch (e) {
      this.failed(ac, e);
    }
  }

  /** The rows of an answer, with their weight charged. Throws if the read was dropped meanwhile. */
  private async paid(answer: Promise<UserFill[] | null>, ac: AbortController): Promise<UserFill[]> {
    const got = await answer;
    if (ac.signal.aborted) throw abortError();
    const rows = Array.isArray(got) ? got : [];
    this.deps.pacer.charge(Math.floor(rows.length / FILL_ROWS_PER_WEIGHT));
    return rows;
  }
}
